import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useVisitorTracking } from "@/hooks/useVisitorTracking";
import { AuthProvider } from "@/hooks/useAuth";
import { SubscriptionProvider } from "@/hooks/useSubscription";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import ScrollToTop from "@/components/ScrollToTop";
import CookieConsent from "@/components/CookieConsent";
import { CartProvider } from "@/contexts/CartContext";
import CartDrawer from "@/components/CartDrawer";
import Checkout from "./pages/Checkout";
import Pricing from "./pages/Pricing";
import CheckoutReturn from "./pages/CheckoutReturn";

import Index from "./pages/Index";
import Nexora from "./pages/Nexora";
import NeilMethod from "./pages/NeilMethod";
import NeilAssessment from "./pages/NeilAssessment";
import RaaS from "./pages/RaaS";
import Enterprise from "./pages/Enterprise";
import AIWorkforce from "./pages/AIWorkforce";
import About from "./pages/About";
import Accessories from "./pages/Accessories";
import Affiliate from "./pages/Affiliate";
import Robots from "./pages/Robots";
import LandscapeRobotics from "./pages/LandscapeRobotics";
import LandscapeProduct from "./pages/LandscapeProduct";
import PuduProduct from "./pages/PuduProduct";
import HumanoidRobotics from "./pages/HumanoidRobotics";
import HumanoidHealthcare from "./pages/HumanoidHealthcare";
import LandscapingIndustry from "./pages/LandscapingIndustry";
import AIAutomation from "./pages/AIAutomation";
import AISocialCampaign from "./pages/AISocialCampaign";
import CloneCreator from "./pages/CloneCreator";
import SeedanceVideo from "./pages/SeedanceVideo";
import CustomerPortal from "./pages/CustomerPortal";
import CC1Pro from "./pages/CC1Pro";
import CC1 from "./pages/CC1";
import MT1Max from "./pages/MT1Max";
import MT1 from "./pages/MT1";
import SH1 from "./pages/SH1";
import PuduD9 from "./pages/PuduD9";
import PuduD7 from "./pages/PuduD7";
import FlashBotArm from "./pages/FlashBotArm";
import BellaBotPro from "./pages/BellaBotPro";
import T300Series from "./pages/T300Series";
import KettyBotPro from "./pages/KettyBotPro";
import HolaBot from "./pages/HolaBot";
import PuduT600 from "./pages/PuduT600";
import MT1Vac from "./pages/MT1Vac";
import BellaBot from "./pages/BellaBot";
import PuduBot2 from "./pages/PuduBot2";
import FlashBotMax from "./pages/FlashBotMax";
import SwiftBot from "./pages/SwiftBot";
import PuduD5 from "./pages/PuduD5";
import PuduBG1 from "./pages/PuduBG1";
import Restaurants from "./pages/Restaurants";
import Hotels from "./pages/Hotels";
import Healthcare from "./pages/Healthcare";
import Warehouses from "./pages/Warehouses";
import Education from "./pages/Education";
import CaseStudies from "./pages/CaseStudies";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Careers from "./pages/Careers";
import RobotRecommender from "./pages/RobotRecommender";
import Analytics from "./pages/Analytics";
import AdminDashboard from "./pages/AdminDashboard";
import Auth from "./pages/Auth";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import BlogCategory from "./pages/BlogCategory";
import BookDemo from "./pages/BookDemo";
import ROICalculatorPage from "./pages/ROICalculatorPage";
import SupportTicket from "./pages/SupportTicket";
import SupportAssistant from "./pages/SupportAssistant";
import AdminSupportTickets from "./pages/AdminSupportTickets";
import Churches from "./pages/Churches";
import TeamMemberProfile from "./pages/TeamMemberProfile";
import Team from "./pages/Team";
import NotFound from "./pages/NotFound";
import Support from "./pages/Support";
import SupportTroubleshoot from "./pages/SupportTroubleshoot";
import SupportKBArticle from "./pages/SupportKBArticle";
import SupportAcademy from "./pages/SupportAcademy";
import Videos from "./pages/Videos";
import FloatingSupportButton from "./components/support/FloatingSupportButton";
import FloatingChatWidget from "./components/FloatingChatWidget";


const queryClient = new QueryClient();

const AppContent = () => {
  useVisitorTracking();
  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <SubscriptionProvider>
            <CartProvider>
            <ScrollToTop />
            <AppContent />
            <CartDrawer />
            <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/nexora" element={<Nexora />} />
            <Route path="/raas" element={<RaaS />} />
            <Route path="/neil-method" element={<NeilMethod />} />
            <Route path="/neil-assessment" element={<NeilAssessment />} />
            <Route path="/enterprise" element={<Enterprise />} />
            <Route path="/ai-workforce" element={<AIWorkforce />} />
            <Route path="/about" element={<About />} />
            <Route path="/accessories" element={<Accessories />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/checkout/return" element={<CheckoutReturn />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/affiliate" element={<Affiliate />} />
            <Route path="/robots" element={<Robots />} />
            <Route path="/robots/landscape" element={<LandscapeRobotics />} />
            <Route path="/robots/landscape/:slug" element={<LandscapeProduct />} />
            <Route path="/robots/gt3" element={<PuduProduct slug="gt3" />} />
            <Route path="/robots/gt5" element={<PuduProduct slug="gt5" />} />
            <Route path="/robots/gt7" element={<PuduProduct slug="gt7" />} />
            <Route path="/robots/mp2000" element={<PuduProduct slug="mp2000" />} />
            <Route path="/robots/et1" element={<PuduProduct slug="et1" />} />
            <Route path="/robots/humanoid" element={<HumanoidRobotics />} />
            <Route path="/robots/humanoid/healthcare" element={<HumanoidHealthcare />} />
            <Route path="/industries/landscaping" element={<LandscapingIndustry />} />
            <Route path="/landscaping" element={<LandscapingIndustry />} />
              <Route path="/ai-automation" element={<AIAutomation />} />
              <Route path="/ai-social-campaign" element={<AISocialCampaign />} />
              <Route path="/sora-2-video" element={<SeedanceVideo />} />
              <Route path="/clone-creator" element={<CloneCreator />} />
              <Route path="/seedance-video" element={<SeedanceVideo />} />
              <Route path="/portal" element={<CustomerPortal />} />
              <Route path="/portal/support" element={<CustomerPortal />} />
            <Route path="/robots/cc1-pro" element={<CC1Pro />} />
            <Route path="/robots/cc1" element={<CC1 />} />
            <Route path="/robots/mt1-max" element={<MT1Max />} />
            <Route path="/robots/mt1" element={<MT1 />} />
            <Route path="/robots/mt1-vac" element={<MT1Vac />} />
            <Route path="/robots/sh1" element={<SH1 />} />
            <Route path="/robots/pudu-d9" element={<PuduD9 />} />
            <Route path="/robots/pudu-d7" element={<PuduD7 />} />
            <Route path="/robots/flashbot-arm" element={<FlashBotArm />} />
            <Route path="/robots/bellabot-pro" element={<BellaBotPro />} />
            <Route path="/robots/t300-series" element={<T300Series />} />
            <Route path="/robots/kettybot-pro" element={<KettyBotPro />} />
            <Route path="/robots/holabot" element={<HolaBot />} />
            <Route path="/robots/pudu-t600" element={<PuduT600 />} />
            <Route path="/robots/mt1-vac" element={<MT1Vac />} />
            <Route path="/robots/bellabot" element={<BellaBot />} />
            <Route path="/robots/pudubot-2" element={<PuduBot2 />} />
            <Route path="/robots/flashbot-max" element={<FlashBotMax />} />
            <Route path="/robots/swiftbot" element={<SwiftBot />} />
            <Route path="/robots/pudu-d5" element={<PuduD5 />} />
            <Route path="/robots/pudu-bg1" element={<PuduBG1 />} />
            <Route path="/restaurants" element={<Restaurants />} />
            <Route path="/hotels" element={<Hotels />} />
            <Route path="/healthcare" element={<Healthcare />} />
            <Route path="/warehouses" element={<Warehouses />} />
            <Route path="/education" element={<Education />} />
            <Route path="/case-studies" element={<CaseStudies />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/robot-recommender" element={<RobotRecommender />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/videos" element={<Videos />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/team" element={<Team />} />
            <Route path="/team/:slug" element={<TeamMemberProfile />} />
            <Route path="/auth" element={<Auth />} />
            <Route 
              path="/analytics" 
              element={

                <ProtectedRoute requireAdmin>
                  <Analytics />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute requireAdmin>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
            {/* Support Center */}
            <Route path="/support" element={<Support />} />
            <Route path="/support/troubleshoot/:slug" element={<SupportTroubleshoot />} />
            <Route path="/support/kb/:slug" element={<SupportKBArticle />} />
            <Route path="/support/academy" element={<SupportAcademy />} />
            <Route path="/support/ticket" element={<SupportTicket />} />
            <Route path="/support/ai-assistant" element={<SupportAssistant />} />
            <Route path="/admin/support-tickets" element={<ProtectedRoute requireAdmin><AdminSupportTickets /></ProtectedRoute>} />
            {/* New CTA pages */}
            <Route path="/book-demo" element={<BookDemo />} />
            <Route path="/roi-calculator" element={<ROICalculatorPage />} />
            {/* Blog */}
            <Route path="/blog/category/:slug" element={<BlogCategory />} />
            {/* Solutions */}
            <Route path="/solutions/churches" element={<Churches />} />
            <Route path="/churches" element={<Churches />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
            <FloatingSupportButton />
            <FloatingChatWidget />
            <CookieConsent />
            </CartProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
